import React, { memo, useCallback, useState } from "react";

import { Button } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import { useLocalization } from "@fluent/react";
import DeleteDialog from "./dialogs/DeleteDialog";

import useRenderAlertTerms from "../helpers/terms";

import styles from "../CustomAlerts.module.scss";

interface AlertsListProps {
  accounts: Account[];
  alerts: API.UserSavedSearch[];
  deleteAlert: (alertId: API.SavedSearchId, onFinally?: () => void) => void;
}

const AlertsList = memo(
  ({ accounts, deleteAlert, alerts }: AlertsListProps) => {
    const { l10n } = useLocalization();
    const { renderTerms } = useRenderAlertTerms(accounts);
    const [deleteAlertId, setDeleteAlertId] =
      useState<API.SavedSearchId | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const toggleDeleteDialog = useCallback(
      (alertId?: API.SavedSearchId) => {
        if (!showDeleteDialog) {
          setDeleteAlertId(alertId!);
        } else {
          setDeleteAlertId(null);
        }
        setShowDeleteDialog(!showDeleteDialog);
      },
      [showDeleteDialog, setShowDeleteDialog, setDeleteAlertId]
    );

    const onConfirmDelete = useCallback(() => {
      if (!deleteAlertId) return;

      deleteAlert(deleteAlertId, () => {
        toggleDeleteDialog();
      });
    }, [deleteAlertId, deleteAlert, toggleDeleteDialog]);

    return (
      <>
        <div className={styles.alertListContainer}>
          {alerts.map((alert) => (
            <div key={alert.id} className={styles.alertListItem}>
              <div className={styles.alertListItemContent}>
                {renderTerms(alert.alert_type, alert.query)}
              </div>
              <div className={styles.alertButton}>
                <Button
                  kind="plain"
                  onClick={() => toggleDeleteDialog(alert.id)}
                >
                  {l10n.getString("custom-alerts-button-delete", {}, "Delete")}
                </Button>
              </div>
            </div>
          ))}
        </div>
        {showDeleteDialog ? (
          <DeleteDialog
            isOpen={showDeleteDialog}
            handleConfirm={onConfirmDelete}
            handleClose={toggleDeleteDialog}
          />
        ) : null}
      </>
    );
  }
);

export default AlertsList;
